import { useState, useRef } from 'react';
import api from '../services/api';

export default function DoneModal({ job, onClose, onSuccess }) {
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('upload'); // upload | client
  const inputRef = useRef();

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files).filter((f) => {
      const ext = f.name.split('.').pop().toLowerCase();
      return ['pdf', 'dwg', 'zip', 'kmz'].includes(ext);
    });
    setFiles((prev) => [...prev, ...dropped]);
  }

  function handleFileInput(e) {
    const selected = Array.from(e.target.files).filter((f) => {
      const ext = f.name.split('.').pop().toLowerCase();
      return ['pdf', 'dwg', 'zip', 'kmz'].includes(ext);
    });
    setFiles((prev) => [...prev, ...selected]);
  }

  function removeFile(index) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit() {
    setError('');
    setUploading(true);

    const form = new FormData();
    files.forEach((f) => form.append('files', f));

    try {
      await api.post(`/jobs/${job.project_id}/done`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStep('client'); // job is complete — now offer to submit to the client
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function openSiteTracker() {
    try {
      const { data } = await api.get(`/jobs/${job.project_id}/sf-url`);
      window.open(data.url, '_blank', 'noopener');
    } catch {
      alert('Could not open Site Tracker for this project — you can open the project record manually to send.');
    }
    onSuccess();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <div className="w-full max-w-lg rounded-xl border border-navy-500 p-6" style={{ backgroundColor: '#142236' }}>
        {step === 'upload' ? (
          <>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-white font-bold text-lg">Mark as Done</h2>
                <p className="text-gray-400 text-xs mt-0.5 font-mono">{job.project_id} — {job.project_name}</p>
              </div>
              <button onClick={onClose} className="text-gray-500 hover:text-white transition text-xl leading-none">&times;</button>
            </div>

            {/* Drag & Drop Zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition mb-4 ${
                dragging ? 'border-gold-500 bg-navy-600' : 'border-navy-500 hover:border-gold-500'
              }`}
            >
              <p className="text-gray-400 text-sm mb-1">Drag & drop files here</p>
              <p className="text-gray-500 text-xs">PDF, DWG, ZIP, KMZ</p>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept=".pdf,.dwg,.zip,.kmz"
                className="hidden"
                onChange={handleFileInput}
              />
            </div>

            {/* File List */}
            {files.length > 0 && (
              <ul className="space-y-2 mb-4">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between bg-navy-700 rounded px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase text-gold-500 bg-navy-600 px-2 py-0.5 rounded">
                        {f.name.split('.').pop()}
                      </span>
                      <span className="text-white text-sm truncate max-w-xs">{f.name}</span>
                    </div>
                    <button onClick={() => removeFile(i)} className="text-gray-500 hover:text-red-400 transition text-sm">
                      &times;
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <p className="text-gray-400 text-xs mb-4">
              Files are optional. Any you add upload to Site Tracker; either way, today's date is stamped in <span className="text-gold-500">Drafting (A)</span>.
            </p>

            {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

            <div className="flex gap-3">
              <button
                onClick={handleSubmit}
                disabled={uploading}
                className="flex-1 bg-gold-500 text-navy-900 py-2 rounded font-bold uppercase tracking-wide text-sm hover:bg-gold-400 disabled:opacity-50 transition"
              >
                {uploading ? 'Uploading…' : 'Submit & Mark Done'}
              </button>
              <button
                onClick={onClose}
                disabled={uploading}
                className="px-4 py-2 rounded border border-navy-500 text-gray-400 text-sm hover:text-white hover:border-gray-400 transition"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-3">
            <div className="text-4xl mb-3">✅</div>
            <h2 className="text-white font-bold text-lg mb-1">Job Completed</h2>
            <p className="text-gray-500 text-xs mb-4 font-mono">{job.project_id}</p>
            <p className="text-gray-200 text-sm mb-6">Would you like to submit it to the client?</p>
            <div className="flex gap-3">
              <button
                onClick={openSiteTracker}
                className="flex-1 bg-gold-500 text-navy-900 py-2.5 rounded font-bold uppercase tracking-wide text-sm hover:bg-gold-400 transition"
              >
                Yes — Send to Client
              </button>
              <button
                onClick={onSuccess}
                className="px-4 py-2.5 rounded border border-navy-500 text-gray-400 text-sm hover:text-white hover:border-gray-400 transition"
              >
                Not now
              </button>
            </div>
            <p className="text-gray-500 text-xs mt-4">Opens this project in Site Tracker, where you can pick files and click “Send File to Customer.”</p>
          </div>
        )}
      </div>
    </div>
  );
}
